import { useState, useRef } from 'react';
import { Bug, Camera, CameraOff, Loader2, Send, X } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { getRecentErrors } from '../utils/consoleCapture';

export function BugReportButton() {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [screenshotData, setScreenshotData] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const previewRef = useRef(null);

  const captureScreenshot = async () => {
    setCapturing(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, {
        scale: 0.5,
        useCORS: true,
        logging: false,
        ignoreElements: (el) => el.closest('[data-bug-report-ui]'),
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
      setScreenshotData(dataUrl);
    } catch (err) {
      console.error('Screenshot capture failed:', err);
      toast.error('Could not capture screenshot');
      setIncludeScreenshot(false);
    } finally {
      setCapturing(false);
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    setDescription('');
    setScreenshotData(null);
    if (includeScreenshot) {
      await captureScreenshot();
    }
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error('Please describe the issue');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        description: description.trim(),
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        screen_size: `${window.innerWidth}x${window.innerHeight}`,
        console_errors: getRecentErrors(),
        screenshot: includeScreenshot ? screenshotData : null,
      };
      const { ok, data } = await api.post('/reports', payload);
      if (ok) {
        toast.success('Bug report submitted — thank you!');
        setOpen(false);
      } else {
        toast.error(data?.detail || 'Failed to submit report');
      }
    } catch {
      toast.error('Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleScreenshot = async () => {
    const next = !includeScreenshot;
    setIncludeScreenshot(next);
    if (next && !screenshotData) {
      await captureScreenshot();
    }
  };

  return (
    <div data-bug-report-ui>
      <button
        onClick={handleOpen}
        className="fixed bottom-5 left-5 z-50 w-11 h-11 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
        title="Report a bug"
        data-testid="bug-report-btn"
      >
        <Bug className="w-5 h-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5 text-primary" /> Report a Bug
            </DialogTitle>
            <DialogDescription>
              Describe the issue you encountered. Your browser info and recent errors are captured automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="bug-description">What went wrong?</Label>
              <textarea
                id="bug-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the issue..."
                className="w-full min-h-[100px] px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring resize-y"
                data-testid="bug-description-input"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Screenshot</Label>
                <Button
                  variant="ghost" size="sm" className="h-7 text-xs gap-1.5"
                  onClick={handleToggleScreenshot}
                  data-testid="toggle-screenshot-btn"
                >
                  {includeScreenshot
                    ? <><CameraOff className="w-3.5 h-3.5" /> Don't include</>
                    : <><Camera className="w-3.5 h-3.5" /> Include screenshot</>}
                </Button>
              </div>
              {includeScreenshot && (
                <div className="rounded-md border border-border overflow-hidden bg-muted">
                  {capturing ? (
                    <div className="flex items-center justify-center py-6 gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" /> Capturing...
                    </div>
                  ) : screenshotData ? (
                    <img
                      ref={previewRef}
                      src={screenshotData}
                      alt="Screenshot preview"
                      className="w-full max-h-48 object-contain"
                      data-testid="screenshot-preview"
                    />
                  ) : (
                    <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                      No screenshot
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="text-xs text-muted-foreground space-y-0.5">
              <p>Also captured: page URL, browser info, screen size, recent console errors</p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || capturing} data-testid="submit-bug-report-btn">
              {submitting
                ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</>
                : <><Send className="w-4 h-4 mr-2" /> Submit Report</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
